import React, { useEffect, useRef, useState } from "react";
import { Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import { roleTypes } from "../../../data/roleType";

const StakeholderView = () => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const [stakeholderData, setStakeholderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");

  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  useEffect(() => {
    fetchStakeholderData();
  }, [id]);

  const fetchStakeholderData = async () => {
    try {
      setLoading(true);
      const config = {
        headers: { Authorization: `${token}` },
      };

      const response = await axios.get(
        `${url.backendURL}stakeholder/${id}`,
        config
      );

      setStakeholderData(response.data.response.data);
    } catch (err) {
      handleError(err, "fetch-stakeholder");
    } finally {
      setLoading(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");
      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  if (loading) {
    return (
      <Row>
        <Col xl="12" lg="12">
          <Card>
            <Card.Body>
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: "200px" }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  if (!stakeholderData) {
    return (
      <Row>
        <Col xl="12" lg="12">
          <Card>
            <Card.Body>
              <div className="text-center">
                <h5>Stakeholder not found</h5>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      <Row>
        <Col xl="12" lg="12" ref={alertRef}>
          <Card>
            <Card.Header className="d-flex justify-content-between">
              <div
                className="breadcrumb header-title"
                style={{ cursor: "pointer" }}
              >
                <h4
                  onClick={() => navigate("/dashboard/app/stakeholder-list")}
                  className="text-primary card-title"
                  style={{ cursor: "pointer" }}
                >
                  <span>Stakeholder</span>
                </h4>
                <h4 className="mx-2 card-title">{">"}</h4>
                <h4 className="card-title">View</h4>
              </div>
            </Card.Header>
            <Card.Body>
              <AlertNotificationSimple
                type={alert.type}
                message={alert.message}
                show={alert.show}
                onClose={() => setAlert({ ...alert, show: false })}
              />

              <div className="stakeholder-info mb-4">
                <h5 className="text-primary mb-3">Stakeholder Information</h5>
                <Row>
                  <Col sm={6}>
                    <div className="form-group">
                      <label className="form-label">Stakeholder Name</label>
                      <div className="form-control-plaintext border p-2 rounded">
                        {stakeholderData.name || "-"}
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className="form-group">
                  <label className="form-label">Address</label>
                  <div
                    className="form-control-plaintext border p-2 rounded"
                    style={{ minHeight: "60px" }}
                  >
                    {stakeholderData.address || "-"}
                  </div>
                </div>
              </div>

              <hr className="my-4" />

              <div className="contacts-info">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="text-primary mb-0">Contact Information</h5>
                </div>

                {stakeholderData.contacts &&
                stakeholderData.contacts.length > 0 ? (
                  stakeholderData.contacts.map((contact, index) => (
                    <div
                      key={index}
                      className="contact-item mb-4 p-3"
                      style={{
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Contact {index + 1}</h6>
                      </div>

                      <Row>
                        <Col md={6}>
                          <div className="form-group">
                            <label className="form-label">Title</label>
                            <div className="form-control-plaintext border p-2 rounded">
                              {contact.title || "-"}
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group">
                            <label className="form-label">Contact Name</label>
                            <div className="form-control-plaintext border p-2 rounded">
                              {contact.name || "-"}
                            </div>
                          </div>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <div className="form-group">
                            <label className="form-label">Phone Number</label>
                            <div className="form-control-plaintext border p-2 rounded">
                              {contact.phone_number || "-"}
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="form-group">
                            <label className="form-label">Email</label>
                            <div className="form-control-plaintext border p-2 rounded">
                              {contact.email || "-"}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-4">
                    <p className="text-muted">No contacts available</p>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default StakeholderView;
